import fetchWithAuth from "@/api/secureFetch";

const SCORE_ENDPOINT = "/api/product-creation/score-field";
const FIELD_EVALUATIONS_ENDPOINT = "/api/product-creation/field-evaluations";
// Provider work has a shared 60-second budget with 30-second request ceilings.
// Keep a transport margin for validation and persistence after it completes.
export const SCORE_TIMEOUT_MS = 65_000;

/** Discover fields the backend evaluates for input quality. */
export type ScorableField =
    | "problem"
    | "solution"
    | "personas"
    | "existing_purpose"
    | "existing_capabilities"
    | "existing_users";

export const SCORABLE_FIELDS: readonly ScorableField[] = [
    "problem",
    "solution",
    "personas",
    "existing_purpose",
    "existing_capabilities",
    "existing_users"
];

export interface ScoreDraftIdentity {
    draftId: number;
    draftVersion: number;
}

export type PersistedFieldEvaluationStatus = "ready" | "missing" | "empty";

export interface PersistedFieldEvaluation {
    status: PersistedFieldEvaluationStatus;
    source_hash?: string | null;
    dependency_hash?: string | null;
    quality_status?: "ready" | "missing" | "empty";
    alignment_status?: "ready" | "missing" | "unavailable" | "not_applicable";
    result?: FieldScoreResult;
}

export interface PersistedFieldEvaluationsResult {
    draft_id: number;
    draft_version: number;
    creation_kind: "new" | "existing" | null;
    fields: Record<ScorableField, PersistedFieldEvaluation>;
}

export interface FieldScoreResult {
    field: ScorableField;
    score: number;
    feedback: string;
    quality?: FieldQualityResult;
    alignment?: FieldAlignmentResult;
    evaluation?: FieldEvaluationIdentity;
    draft_id?: number;
    draft_version?: number;
    source_hash?: string;
    vectorized?: boolean;
}

export type FindingStatus = "present" | "partial" | "missing";

export type RelationshipOutcome = "aligned" | "partial" | "unrelated" | "insufficient_information";

export interface FieldFindingEvidence {
    field: string;
    quote: string;
}

export interface FieldFinding {
    code: string;
    criterion: string;
    status: FindingStatus;
    evidence: FieldFindingEvidence[];
    suggestion: string;
    relationshipOutcome?: RelationshipOutcome;
    explanation?: string;
}

export interface FieldQualityResult {
    score: number;
    feedback: string;
    findings: FieldFinding[];
}

export interface FieldAlignmentResult {
    status: "ready" | "unavailable" | "not_applicable";
    score: number | null;
    feedback: string;
    findings: FieldFinding[];
}

export interface FieldEvaluationIdentity {
    sourceHash: string;
    dependencyHash: string;
    rubricVersion: string;
    promptVersion: string;
}

export class FieldScoreApiError extends Error {
    readonly status: number;
    readonly code: string;

    constructor(status: number, code: string, message: string) {
        super(message);
        this.name = "FieldScoreApiError";
        this.status = status;
        this.code = code;
    }
}

export interface ScoreBand {
    label: string;
    color: string;
}

/** Rubric bands, mirroring the score ranges in the backend prompts. */
export function scoreBand(score: number): ScoreBand {
    if (score >= 90) return { label: "Excellent", color: "var(--np-success)" };
    if (score >= 75) return { label: "Good", color: "var(--np-brand)" };
    if (score >= 50) return { label: "Needs Improvement", color: "var(--np-warn)" };
    if (score >= 25) return { label: "Poor", color: "var(--error, #b42318)" };
    return { label: "Very Poor", color: "var(--error, #b42318)" };
}

/** Evaluate one Discover field against its exact persisted draft revision.
 * Both creation and refresh-preview drafts supply this identity; the backend
 * loads the canonical value rather than trusting the request body. */
export async function scoreField(
    field: ScorableField,
    value: string,
    token: string,
    options: { signal?: AbortSignal; timeoutMs?: number; draft?: ScoreDraftIdentity } = {}
): Promise<FieldScoreResult> {
    const controller = new AbortController();
    let timedOut = false;
    const abortFromCaller = () => controller.abort();
    if (options.signal?.aborted) controller.abort();
    else options.signal?.addEventListener("abort", abortFromCaller, { once: true });
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
        timeoutId = globalThis.setTimeout(() => {
            timedOut = true;
            controller.abort();
            reject(new Error("Evaluation timed out — try again."));
        }, options.timeoutMs ?? SCORE_TIMEOUT_MS);
    });

    try {
        const response = await Promise.race([
            fetchWithAuth(SCORE_ENDPOINT, token, {
                method: "POST",
                body: JSON.stringify({
                    field,
                    value,
                    ...(options.draft
                        ? { draft_id: options.draft.draftId, draft_version: options.draft.draftVersion }
                        : {})
                }),
                signal: controller.signal
            }),
            timeoutPromise
        ]);
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            const error = body?.error;
            const message = typeof error === "string" ? error : error?.message;
            const code = typeof error === "object" && typeof error?.code === "string"
                ? error.code
                : "FIELD_EVALUATION_FAILED";
            throw new FieldScoreApiError(response.status, code, message || `Evaluation failed (${response.status})`);
        }
        return response.json();
    } catch (error) {
        const abortedByInternalTimeout = controller.signal.aborted && !options.signal?.aborted;
        if (timedOut || abortedByInternalTimeout) throw new Error("Evaluation timed out — try again.");
        throw error;
    } finally {
        if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
        options.signal?.removeEventListener("abort", abortFromCaller);
    }
}

/** Read every evaluation that is valid for one exact persisted draft.
 *
 * This endpoint never invokes the provider. It lets a resumed form, another
 * concurrent field evaluation, or a server-side carry-forward update hydrate
 * the UI from the backend's canonical source and dependency hashes. */
export async function getPersistedFieldEvaluations(
    token: string,
    draft: ScoreDraftIdentity,
    options: { signal?: AbortSignal } = {}
): Promise<PersistedFieldEvaluationsResult> {
    const response = await fetchWithAuth(FIELD_EVALUATIONS_ENDPOINT, token, {
        method: "POST",
        body: JSON.stringify({ draft_id: draft.draftId, draft_version: draft.draftVersion }),
        ...(options.signal ? { signal: options.signal } : {})
    });
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const error = body?.error;
        const message = typeof error === "string" ? error : error?.message;
        const code = typeof error === "object" && typeof error?.code === "string"
            ? error.code
            : "FIELD_EVALUATIONS_LOAD_FAILED";
        throw new FieldScoreApiError(response.status, code, message || `Evaluation state failed to load (${response.status})`);
    }
    return response.json();
}
